import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow, SectionLabel } from "@/components/Eyebrow";
import { ArrowRight } from "lucide-react";

const ARTICLES = [
  { n: "I",    title: "The Ivy League Isn't the Only Road to Success",                            status: "Published",   to: "/blog/ivy-league-isnt-the-only-road" },
  { n: "II",   title: "The New Rules of College Admissions in the AI Era",                       status: "Published",   to: "/blog/new-rules-college-admissions-ai" },
  { n: "III",  title: "Community College Was the Best Decision I Never Planned to Make",          status: "Published",   to: "/blog/community-college-best-decision" },
  { n: "IV",   title: "I Didn't Feel Like a Failure. I Felt Like I Hadn't Found My Place.",      status: "Published",   to: "/blog/didnt-feel-like-failure" },
  { n: "V",    title: "Stop Shopping for Colleges Like You're Buying a Luxury Brand",            status: "Published",   to: "/blog/stop-shopping-luxury-brand" },
  { n: "VI",   title: "Don't Ask, \u201CWhere Did You Get In?\u201D",                              status: "Published",   to: "/blog/dont-ask-where-did-you-get-in" },
  { n: "VII",  title: "Why Every Student Needs a Second Brain Before Applying to College",        status: "Published",   to: "/blog/second-brain-before-college" },
  { n: "VIII", title: "The Great Exception: What Harvard Extension School Taught Me",             status: "Published",   to: "/blog/great-exception-harvard-extension" },
  { n: "IX",   title: "AI Won't Replace Students. It Will Replace Average Applicants.",          status: "Coming Soon", to: null },
  { n: "X",    title: "What I Wish Every Parent Knew Before Spending $300,000 on College",       status: "Coming Soon", to: null },
];

const COMPANIONS = [
  { title: "The Second Chance That Wore Crimson", to: "/blog/second-chance-that-wore-crimson", note: "Valencia + Harvard Extension School · the American Dream" },
  { title: "Your Most Underrated Admissions Tool Might Be Google NotebookLM", to: "/blog/notebooklm-admissions-tool", note: "Tooling · Companion piece" },
  { title: "The Classroom That Changed My Life, The Country That Made It Possible", to: "/blog/classroom-that-changed-my-life", note: "Personal reflection · Origin" },
];

export default function Playbook() {
  return (
    <Layout>
      <SEO
        title="The New Ivy Playbook — A 10-Part Series by Taimur Ahmed"
        description="A 10-article series redefining college admissions, the value of community college, and the rise of strategic Florida transfer pathways in the AI economy."
        canonical="/playbook"
        keywords="new ivy playbook, admissions strategy, community college essays, AI admissions, Taimur Ahmed"
      />
      <section className="bg-ivy text-bone border-b border-ivy-light" data-testid="playbook-page">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 lg:pt-28 pb-20">
          <Eyebrow tone="bone" className="text-brass-soft">From the Founder · 10-Part Series</Eyebrow>
          <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[5.25rem] text-bone leading-[1.02] tracking-tightest mt-6">
            The New Ivy<br /><span className="italic text-brass-soft">Playbook.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base sm:text-lg text-bone-dim leading-relaxed">
            A 10-article series by Taimur Ahmed redefining college admissions, the value of community college, and the rise of strategic Florida transfer pathways in the AI economy.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20">
        <SectionLabel index="01" label="The Series" />
        <div className="mt-10 border-t border-l border-stone/40" data-testid="playbook-articles-table">
          {ARTICLES.map((a) => {
            const Inner = (
              <div className="border-r border-b border-stone/40 grid grid-cols-12 items-baseline gap-4 p-6 bg-bone hover:bg-bone-dim transition-colors group">
                <div className="col-span-2 sm:col-span-1 font-serif text-brass text-2xl tabular-nums">{a.n}</div>
                <h3 className="col-span-8 sm:col-span-9 font-serif text-lg sm:text-xl text-ink tracking-tight group-hover:text-ivy transition-colors">{a.title}</h3>
                <div className="col-span-2 text-right">
                  <span className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${a.status === "Published" ? "text-ivy" : "text-stone"}`}>
                    {a.status}
                  </span>
                </div>
              </div>
            );
            return a.to ? (
              <Link key={a.n} to={a.to} className="block" data-testid={`playbook-article-${a.n}`}>{Inner}</Link>
            ) : (
              <div key={a.n} data-testid={`playbook-article-${a.n}`}>{Inner}</div>
            );
          })}
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="h-px bg-brass" />
      </div>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20">
        <SectionLabel index="02" label="Companion Essays" />
        <div className="mt-10 grid lg:grid-cols-3 gap-0 border-t border-l border-stone/40" data-testid="playbook-companions">
          {COMPANIONS.map((c) => (
            <Link key={c.to} to={c.to} className="border-r border-b border-stone/40 bg-bone p-8 hover:bg-bone-dim transition-colors group">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">{c.note}</div>
              <h3 className="font-serif font-medium text-xl text-ink mt-3 tracking-tight leading-snug group-hover:text-ivy transition-colors">{c.title}</h3>
              <div className="mt-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy group-hover:text-brass transition-colors">Read <ArrowRight className="h-3 w-3" /></div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-ivy text-bone border-y border-ivy-light">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 text-center">
          <Eyebrow tone="bone" className="text-brass-soft">Start Here</Eyebrow>
          <h2 className="mt-5 font-serif font-medium text-3xl sm:text-4xl tracking-tight leading-tight">See where you stand against the playbook.</h2>
          <Link to="/transfer-audit" className="inline-block mt-10 bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 transition-colors" data-testid="playbook-audit-cta">Schedule a Transfer Audit — $249</Link>
        </div>
      </section>
    </Layout>
  );
}
